import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import {
  RefreshCw,
  Search,
  Activity,
  User,
  CheckCircle2,
  XCircle,
  Trash2,
  Mail,
  FileText,
  ShieldAlert,
  Download as DownloadIcon,
  FileEdit,
  Wand2,
  Image as ImageIcon,
} from "lucide-react";

/* Map of action → {icon, color, label} */
const ACTION_META = {
  status_change: { icon: CheckCircle2, color: "emerald", label: "Status change" },
  mark_paid: { icon: CheckCircle2, color: "emerald", label: "Marked paid" },
  delete: { icon: Trash2, color: "rose", label: "Submission deleted" },
  bulk_approve: { icon: CheckCircle2, color: "emerald", label: "Bulk approve" },
  bulk_reject: { icon: XCircle, color: "rose", label: "Bulk reject" },
  bulk_under_review: { icon: Activity, color: "amber", label: "Bulk under review" },
  bulk_delete: { icon: Trash2, color: "rose", label: "Bulk delete" },
  export_submissions_csv: { icon: DownloadIcon, color: "sky", label: "Submissions CSV exported" },
  export_analytics_csv: { icon: DownloadIcon, color: "sky", label: "Analytics CSV exported" },
  kb_create: { icon: FileEdit, color: "violet", label: "KB entry created" },
  kb_update: { icon: FileEdit, color: "violet", label: "KB entry updated" },
  kb_delete: { icon: Trash2, color: "rose", label: "KB entry deleted" },
  blog_create: { icon: FileText, color: "indigo", label: "Blog post created" },
  blog_update: { icon: FileText, color: "indigo", label: "Blog post updated" },
  blog_delete: { icon: Trash2, color: "rose", label: "Blog post deleted" },
  blog_auto_generate: { icon: Wand2, color: "fuchsia", label: "Blog auto-generated" },
  blog_regen_hero: { icon: ImageIcon, color: "fuchsia", label: "Hero image regenerated" },
};

const COLOR_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rose: "bg-rose-100 text-rose-700 border-rose-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  sky: "bg-sky-100 text-sky-700 border-sky-200",
  violet: "bg-violet-100 text-violet-700 border-violet-200",
  indigo: "bg-indigo-100 text-indigo-700 border-indigo-200",
  fuchsia: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function fmtFull(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default function AdminAuditPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [actionFilter, setActionFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/audit-logs", { params: { limit: 500 } });
      setItems(r.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let rows = items;
    if (actionFilter !== "all") {
      rows = rows.filter((r) => r.action === actionFilter);
    }
    if (q.trim()) {
      const needle = q.toLowerCase().trim();
      rows = rows.filter(
        (r) =>
          (r.actor || "").toLowerCase().includes(needle) ||
          (r.action || "").toLowerCase().includes(needle) ||
          (r.target_id || "").toLowerCase().includes(needle) ||
          JSON.stringify(r.meta || {}).toLowerCase().includes(needle),
      );
    }
    return rows;
  }, [items, q, actionFilter]);

  // Distinct actions for filter chips
  const actionsList = useMemo(() => {
    const set = new Set(items.map((i) => i.action));
    return Array.from(set).sort();
  }, [items]);

  return (
    <div className="px-2 sm:px-3 md:px-6 py-4 md:py-6 space-y-5" data-testid="admin-audit-page">
      {/* Page header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            Audit log
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every admin action — status changes, deletes, exports, bulk ops, blog & KB edits — recorded in order.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="audit-refresh"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Search + action filter chips */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search actor, action, target id, meta…"
            data-testid="audit-search"
            className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-slate-400 outline-none text-sm"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 scrollbar-none">
          <FilterChip
            label={`All · ${items.length}`}
            active={actionFilter === "all"}
            onClick={() => setActionFilter("all")}
            testid="audit-filter-all"
          />
          {actionsList.map((act) => {
            const meta = ACTION_META[act];
            const Icon = meta?.icon || ShieldAlert;
            const count = items.filter((i) => i.action === act).length;
            return (
              <FilterChip
                key={act}
                label={meta?.label || act}
                count={count}
                active={actionFilter === act}
                onClick={() => setActionFilter(act)}
                Icon={Icon}
                color={meta?.color || "slate"}
                testid={`audit-filter-${act}`}
              />
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 text-center" data-testid="audit-empty">
            <Activity className="w-10 h-10 mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500 text-sm">
              {loading ? "Loading…" : "No audit entries match your filter."}
            </p>
          </div>
        ) : (
          <ol className="divide-y divide-slate-100">
            {filtered.map((r, i) => {
              const meta = ACTION_META[r.action] || { icon: ShieldAlert, color: "slate", label: r.action };
              const Icon = meta.icon;
              const colorClass = COLOR_CLASSES[meta.color] || COLOR_CLASSES.slate;
              return (
                <li
                  key={r.id || i}
                  data-testid={`audit-row-${i}`}
                  className="px-3 sm:px-5 py-3.5 hover:bg-slate-50/60 flex items-start gap-3"
                >
                  <div
                    className={`w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0 ${colorClass}`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                      <span className="font-semibold text-slate-900">{meta.label}</span>
                      {r.action === "status_change" && r.meta?.to && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-slate-100 text-slate-700">
                          → {r.meta.to}
                        </span>
                      )}
                      {r.meta?.count && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-slate-100 text-slate-700">
                          ×{r.meta.count}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {r.actor || "system"}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Mail className="w-3 h-3 opacity-50" />
                        <span className="font-mono text-[11px] truncate max-w-[120px] sm:max-w-[200px]">
                          {r.target_type}:{(r.target_id || "").slice(0, 14)}
                          {(r.target_id || "").length > 14 ? "…" : ""}
                        </span>
                      </span>
                      <span title={fmtFull(r.timestamp)}>{fmtRelative(r.timestamp)}</span>
                    </div>
                    {r.meta && Object.keys(r.meta).length > 0 && (
                      <details className="mt-1.5 text-[11px] text-slate-500">
                        <summary className="cursor-pointer select-none hover:text-slate-700">
                          metadata
                        </summary>
                        <pre className="mt-1 p-2 rounded-md bg-slate-50 border border-slate-100 font-mono text-[10px] overflow-x-auto whitespace-pre-wrap">
                          {JSON.stringify(r.meta, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <p className="text-xs text-slate-400 px-1">
        Showing latest {items.length} entries. Audit rows are immutable and retained indefinitely.
      </p>
    </div>
  );
}

function FilterChip({ label, count, active, onClick, Icon, color = "slate", testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 ${
        active
          ? "bg-slate-900 text-white border-slate-900 shadow-sm"
          : `bg-white text-slate-700 border-slate-200 hover:border-slate-400 ${
              Icon ? `${COLOR_CLASSES[color]?.split(" ")[0] || ""}` : ""
            }`
      }`}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span>{label}</span>
      {count !== undefined && (
        <span className={`text-[10px] opacity-70 ${active ? "" : "text-slate-500"}`}>
          ({count})
        </span>
      )}
    </button>
  );
}
